import { MenuItem } from '@mui/material';
import type { TextFieldProps } from '@side/pantry';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLicensedStateContext } from '@/contexts/LicensedStateContext';
import { DropdownLoadingIcon } from '../../DropdownLoadingIcon';
import { FormTextField } from '../../Inputs/FormTextField';

type Props = TextFieldProps & {
  options: (string | undefined)[];
};

export const StateDropdown = ({ options, ...rest }: Props) => {
  const loading = !options.length;
  const onlyOneOption = options.length === 1;
  const { stateError, redirectStateError, alertType } =
    useLicensedStateContext();
  const { setValue } = useFormContext();

  useEffect(() => {
    if (onlyOneOption) {
      setValue('state', options[0], {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [onlyOneOption, setValue, options]);

  return (
    <FormTextField
      select
      name="state"
      label="State"
      disabled={loading || onlyOneOption}
      slotProps={{
        input: {
          endAdornment: loading ? <DropdownLoadingIcon /> : null,
        },
      }}
      fullWidth
      required
      disableRequiredHighlight
      helperText={
        stateError || (redirectStateError && alertType === 'error')
          ? 'Invalid state'
          : ''
      }
      error={!!stateError || (!!redirectStateError && alertType === 'error')}
      {...rest}
    >
      {options.map((stateOption) => (
        <MenuItem
          key={stateOption}
          value={stateOption}
          data-testid={`state-option-${stateOption}`}
        >
          {stateOption}
        </MenuItem>
      ))}
    </FormTextField>
  );
};
